import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';

import {
    type ButtonHookProps,
    type ButtonResult,
    type GenericButtonHookProps,
    useButton,
} from '../Button';
import {
    type CardHookProps,
    CardProvider,
    type CardResult,
    type GenericCardHookProps,
    useCard,
} from './Card';
import { CardDismissButton } from './CardDismissButton';

export type GenericCardButtonHookProps = GenericButtonHookProps & GenericCardHookProps;
export type CardButtonHookProps = ButtonHookProps & CardHookProps;
export type CardButtonResult = ButtonResult['buttonProps'] & CardResult;

export type CardButtonProps = GenericCardButtonHookProps & React.ComponentPropsWithoutRef<'div'>;

/**
 * Card button component to use a pressable card.
 */
export const CardButton = React.forwardRef<HTMLDivElement, CardButtonProps>(function CardButton(
    {
        disabled,
        onPress,
        preventFocusOnPress,
        onPressStart,
        className,
        onDismiss,
        kind,
        ...props
    }: CardButtonProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const cardButtonProps = useCardButton({
        elementType: 'div',
        onPress,
        onPressStart,
        preventFocusOnPress,
        disabled,
        className,
        kind,
        headingId,
        descriptionId,
    });

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <div ref={ref} {...props} {...cardButtonProps}>
                {onDismiss ? <CardDismissButton onDismiss={onDismiss} /> : null}
                {props.children}
            </div>
        </CardProvider>
    );
});

/**
 * Low-level hook to create a card button component.
 */
export function useCardButton(props: CardButtonHookProps): CardButtonResult {
    const { className } = useCard(props);
    const { buttonProps, isDisabled, isFocusVisible, isPressed } = useButton(props);

    return {
        ...buttonProps,
        className: clsx(
            className,
            isDisabled
                ? 'opacity-disabled'
                : clsx(
                      'cursor-pointer transition-shadow ease-in-out',
                      isFocusVisible && 'ring-2 ring-focus',
                      !isPressed && 'hover:shadow-elevated'
                  ),
            props.className
        ),
    };
}
